import type { UserJob } from '../types';

/**
 * Client-side job search predicate used by the Dashboard's sidebar search box.
 *
 * EVERY field access is defensive on purpose. A job card can legitimately reach
 * the list without a `jobtitle` or `jobcompany` — placeholder / "Research in
 * Progress" cards are created before the board is parsed, and older records
 * predate some fields. This predicate runs inside a `useMemo` during render, so
 * a TypeError here does not fail gracefully: React escalates it to the
 * top-level error boundary and the whole app is replaced with "Something went
 * wrong". One malformed card must never be able to do that.
 */
export function jobMatchesQuery(job: Partial<UserJob> | null | undefined, query: string): boolean {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return true;
  if (!job) return false;

  const has = (value: unknown) => String(value ?? '').toLowerCase().includes(q);

  return (
    has(job.jobtitle) ||
    has(job.jobcompany) ||
    has(job.joblocation) ||
    (Array.isArray(job.jobtags) && job.jobtags.some((tag) => has(tag)))
  );
}
